import { useCallback, useEffect, useState } from 'react';
import { store } from 'redux/store';
import { Encumbrance } from 'types';
import { getEncumbrances } from '../redux/encumbranceActions';

type UseGetEncumbrancesProps = {
  shareId?: string;
  societyId?: string;
  page?: number;
  rowsPerPage?: number;
  updateState?: boolean;
};

export const useGetEncumbrances = ({
  shareId,
  societyId,
  page = 1,
  rowsPerPage = 10,
  updateState = true,
}: UseGetEncumbrancesProps) => {
  const [encumbrances, setEncumbrances] = useState<Encumbrance[]>([]);
  const [totalEncumbrances, setTotalEncumbrances] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchEncumbrances = useCallback(
    async (controller: AbortController) => {
      if (!shareId && !societyId) return;

      try {
        setIsLoading(true);

        const response = await store.dispatch(
          getEncumbrances({
            shareId,
            societyId,
            params: { page, items: rowsPerPage },
            updateState,
            signal: controller.signal,
          })
        );

        if (!controller.signal.aborted) {
          setEncumbrances(response.data);
          setTotalEncumbrances(response.total);
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    [shareId, societyId, page, rowsPerPage, updateState]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchEncumbrances(controller);
    return () => controller.abort();
  }, [fetchEncumbrances]);

  return {
    data: {
      encumbrances,
      totalEncumbrances,
    },
    isLoading,
    isError,
  };
};
